import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { liveTrace } from 'src/app/commons/utils/live-trace';
import { Client, IFrame, Message, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { Capacitor } from '@capacitor/core';
import { hasWebSession } from 'src/app/commons/utils/web-session';
import { App } from '@capacitor/app';
import { NGXLogger } from 'ngx-logger';
import { Observable, Subject, Subscription, debounceTime, filter } from 'rxjs';
import { AppConstants } from 'src/app/commons/app-constants';

export type DashboardEventSection =
  'FINANCIAL' | 'RECONCILIATION' | 'INVENTORY' | 'MILL' | 'MY_TASKS' | 'ACTIVITY' | 'TALLY' | 'CONSIGNMENT'
  /** 2026-09-28: a support ticket changed — header badge only. */
  | 'TICKETS'
  /** 2026-09-28: Sales CRM inbox / campaign changed — CRM screens only (sent to CRM staff only). */
  | 'CRM_INBOX' | 'CRM_CAMPAIGN'
  /** 2026-09-30: the company's team changed (Admin made / removed) — Manage User list only. */
  | 'TEAM'
  /** 2026-09-30: a Business Partner / Product / Loading Site / Quality Grade changed — those list screens. */
  | 'MASTER_DATA'
  /** 2026-09-30: a tally was created or deleted (NOT its rows) — what the Dashboard / Command Center totals need. */
  | 'TALLY_STATUS'
  /** 2026-10-04: a bank transfer / cheque payment changed — the Offline Payments list and the waiting-payment panels. */
  | 'OFFLINE_PAY'
  /** 2026-10-05: a customer screen-support session changed — the ticket screens' screen-support card, the support list and the customer's prompt. */
  | 'SCREEN_SUPPORT'
  /** Not from the server: "catch up now, reload everything" (server restarted, or no change numbers). */
  | 'RESYNC';

/** One batched change message (server groups ~1 s of changes per company), or a catch-up. */
export interface LiveChange {
  sections: DashboardEventSection[];
  /** Tallies whose rows/totals changed in this batch. */
  tallyMasterIds: number[];
  /** true = listeners that filter by tally ids should reload anyway (catch-up without ids). */
  resync: boolean;
}

interface LiveVersions { bootId?: string; versions?: Record<string, number>; }

/** One server-side step of a long job (e.g. AI Import), sent only to the user who started it. */
export interface LiveProgress {
  progressId: string;
  /** AI Import: RECEIVED | PREPARING_PAGE | READING | READING_PAGE */
  stage: string;
  page: number;
  pages: number;
  /** PAGE_DONE: rows found on that page. */
  rows?: number;
  /** READING_PAGE of a PDF: small preview of the page being read (data URL). */
  thumb?: string;
}

/**
 * THE app's live connection (LIVE_UPDATES_NO_POLLING_PLAN.md step 2, 2026-09-28).
 *
 * One STOMP connection for the whole app, opened after login (start(), from NotificationSocketService.init)
 * and closed on logout (shutdown()). It carries both per-user queues:
 *  - /user/queue/dashboard-updates → changes$ / events$ / watch() (screens reload only what changed);
 *  - /user/queue/notifications     → notifications$ (bell, role change, forced logout).
 * Before 2026-09-28 the bell had its own second connection plus a 30 s REST poll while it was down.
 *
 * No timers except the STOMP heartbeat (10 s each way; the server sends its side too). Catch-up is
 * event-driven: after a reconnect, when the tab becomes visible, and when the phone app resumes, it asks
 * GET /api/v1/live/versions and compares the company's change numbers with the last ones seen — only the
 * sections that moved are announced; a new server bootId (restart) announces RESYNC once. Reconnect tries
 * stop while offline (resume on `online`) and are capped at 25 s apart.
 *
 * Screens may still call connect()/disconnect() (reference-counted) — the connection simply stays open
 * while the app has started it.
 */
@Injectable({ providedIn: 'root' })
export class DashboardSocketService {
  private readonly logger = inject(NGXLogger);
  private readonly ngZone = inject(NgZone);
  private readonly http = inject(HttpClient);

  private client: Client | null = null;
  private readonly changesSubject = new Subject<LiveChange>();
  private readonly eventsSubject = new Subject<DashboardEventSection>();
  private readonly notificationSubject = new Subject<string>();
  private readonly progressSubject = new Subject<LiveProgress>();
  private readonly connectedSubject = new Subject<{ first: boolean }>();

  /** Full batched change messages (+ catch-ups). */
  readonly changes$: Observable<LiveChange> = this.changesSubject.asObservable();

  /** One emission per changed section (+ 'RESYNC'). TALLY is left out on purpose: dashboards reload
   *  everything on any event here, and tally rows are saved every few seconds while someone is tallying.
   *  TICKETS and CRM_* too: only their own screens care (they use watch(), which reads changes$). */
  readonly events$: Observable<DashboardEventSection> = this.eventsSubject.asObservable()
    .pipe(filter((s) => !DashboardSocketService.NOT_FOR_DASHBOARDS.includes(s)));
  private static readonly NOT_FOR_DASHBOARDS: DashboardEventSection[] = ['TALLY', 'TICKETS', 'CRM_INBOX', 'CRM_CAMPAIGN', 'TEAM', 'OFFLINE_PAY', 'SCREEN_SUPPORT'];

  /** Raw bodies of /user/queue/notifications messages. */
  readonly notifications$: Observable<string> = this.notificationSubject.asObservable();
  /** Server-side steps of this user's own long jobs, e.g. AI Import "reading page 3 of 8"
   *  (/user/queue/progress — UPLOAD_PROGRESS_PLAN.md, 2026-09-29). Match on progressId. */
  readonly progress$: Observable<LiveProgress> = this.progressSubject.asObservable();
  /** After every successful connect (first = the first one since start / reconnect after a drop). */
  readonly connected$: Observable<{ first: boolean }> = this.connectedSubject.asObservable();

  private refCount = 0;
  private pinned = false;
  private everConnected = false;
  private lostConnection = false;
  private listenersOn = false;
  private bootId: string | null = null;
  private versions: Record<string, number> = {};
  private versionsKnown = false;
  private catchUpInFlight = false;

  private backoffMs = 5000;
  private static readonly BASE_RECONNECT_MS = 5000;
  /** Reconnect tries are capped at the heartbeat-ish beat (plan section 3). */
  private static readonly MAX_RECONNECT_MS = 25000;
  private static readonly SESSION_PROBE_MIN_MS = 60000;
  private lastSessionProbeAt = 0;
  private static readonly VERSIONS_URL = `${AppConstants.API_URL}api/v1/live/versions`;

  /** One-liner for screens: reload when any of `sections` changes (or on RESYNC), right away — the server
   *  already sends ONE message per saved change, so there is nothing to wait for (pass debounceMs only if a
   *  screen really needs it). Announces interest in `sections` to the server, so this user is sent only what
   *  their open screens use. Connects now; unsubscribing the returned Subscription (in ngOnDestroy) also
   *  disconnects and withdraws the interest. */
  watch(sections: DashboardEventSection[], onChange: () => void, debounceMs = 0): Subscription {
    this.connect();
    // TALLY never triggers watch() (as before — rows are saved every few seconds while tallying), so it is
    // neither listened for nor announced; screens that follow tally rows use changes$ + declareInterest.
    const wanted: DashboardEventSection[] = sections.filter((s) => s !== 'TALLY');
    const release = this.declareInterest(wanted);
    const source = this.changes$.pipe(
      filter((c) => c.sections.includes('RESYNC') || c.sections.some((s) => wanted.includes(s))),
    );
    const sub = (debounceMs > 0 ? source.pipe(debounceTime(debounceMs)) : source).subscribe(() => onChange());
    sub.add(() => this.disconnect());
    sub.add(release);
    return sub;
  }

  // ── Interest (which sections this user's open screens need) ───────────────────────────────
  // A screen tells the server what it uses by subscribing to a marker destination per section
  // (/topic/live-interest/MILL ...). Nothing is ever sent to those — the server's broker keeps the list of
  // who subscribed, and skips sections nobody here asked for. A connection with no marker yet is sent
  // everything, so a screen that forgets to declare still works (just with more messages).

  private static readonly INTEREST_PREFIX = '/topic/live-interest/';
  private readonly interest = new Map<string, number>();
  private markerSubs = new Map<string, StompSubscription>();

  /** Declare the sections a screen needs. Returns the function that withdraws them (call on destroy). */
  declareInterest(sections: DashboardEventSection[]): () => void {
    const wanted = [...new Set(sections)].filter((s) => s !== 'RESYNC');
    wanted.forEach((s) => {
      const n = (this.interest.get(s) ?? 0) + 1;
      this.interest.set(s, n);
      if (n === 1) this.subscribeMarker(s);
    });
    let released = false;
    return () => {
      if (released) return;
      released = true;
      wanted.forEach((s) => {
        const n = (this.interest.get(s) ?? 0) - 1;
        if (n <= 0) {
          this.interest.delete(s);
          this.unsubscribeMarker(s);
        } else {
          this.interest.set(s, n);
        }
      });
    };
  }

  private subscribeMarker(section: string): void {
    const c = this.client;
    if (!c?.connected || this.markerSubs.has(section)) return;   // not connected yet: onConnect subscribes all
    try {
      this.markerSubs.set(section, c.subscribe(DashboardSocketService.INTEREST_PREFIX + section, () => { /* marker only */ }));
    } catch { /* best effort — worst case this connection is treated as "interested in everything" */ }
  }

  private unsubscribeMarker(section: string): void {
    const s = this.markerSubs.get(section);
    this.markerSubs.delete(section);
    try { s?.unsubscribe(); } catch { /* connection already gone */ }
  }

  /** App-wide: keep the connection open until shutdown() (logout). Idempotent. */
  start(): void {
    this.pinned = true;
    this.ensureOpen();
  }

  /** Logout: close the connection and forget change numbers (next login starts clean). */
  shutdown(): void {
    this.pinned = false;
    this.refCount = 0;
    this.interest.clear();
    this.teardown();
    this.bootId = null;
    this.versions = {};
    this.versionsKnown = false;
  }

  connect(): void {
    this.refCount++;
    this.ensureOpen();
  }

  disconnect(): void {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.wanted) return;
    this.teardown();
  }

  private get wanted(): boolean {
    return this.pinned || this.refCount > 0;
  }

  /** Nobody signed in (before login, after logout, forced logout, expired session): a handshake can only be refused (401). */
  private hasSession(): boolean {
    return hasWebSession();
  }

  private ensureOpen(): void {
    this.registerListeners();
    if (this.client?.active) return;
    if (!this.hasSession()) return;   // not signed in (login, sign-up, Check Your Email): do not knock
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return; // wait for `online`
    this.openClient();
  }

  private teardown(): void {
    const client = this.client;
    this.client = null;
    this.markerSubs.clear();   // they die with the connection; onConnect re-creates them
    this.everConnected = false;
    this.lostConnection = false;
    this.ngZone.runOutsideAngular(() => {
      try { client?.deactivate(); } catch { /* best-effort teardown */ }
    });
  }

  /**
   * A refused connection (expired or missing login cookie) can only be repaired by a normal HTTP call: the access cookie lasts
   * 2 h and only the HTTP interceptor refreshes it on a 401. This screen has no polling, so without this the live connection
   * stayed dead until the user tapped something else. One cheap authenticated call, at most once a minute while the connection
   * is down; if it succeeds (cookie refreshed) we reconnect straight away. The answer itself is ignored on purpose, so the
   * change-number baseline used by catch-up is not moved.
   */
  private probeSession(): void {
    const now = Date.now();
    if (!this.wanted || this.client?.connected || this.catchUpInFlight) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    if (now - this.lastSessionProbeAt < DashboardSocketService.SESSION_PROBE_MIN_MS) return;
    this.lastSessionProbeAt = now;
    this.ngZone.run(() => {
      this.http.get(DashboardSocketService.VERSIONS_URL).subscribe({
        next: () => this.reconnectNow('session checked'),
        error: () => { /* truly expired: the interceptor shows the normal sign-in flow */ },
      });
    });
  }

  /** Try again right away (network back / app resumed / tab visible) instead of waiting out the back-off. */
  private reconnectNow(reason: string): void {
    if (!this.wanted || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
    if (this.client?.connected) return;
    liveTrace('live connection', 'event', 'reconnect now: ' + reason);
    const old = this.client;
    this.client = null;
    if (this.everConnected) this.lostConnection = true;
    this.ngZone.runOutsideAngular(() => { try { old?.deactivate(); } catch { /* ignore */ } });
    this.openClient();
  }

  private openClient(): void {
    if (this.client?.active) return;

    // The ENTIRE client — activation, the SockJS transport churn, and every reconnect attempt —
    // runs OUTSIDE Angular's zone. Confirmed root cause of the "User Profile stuck on loader"
    // hang (2026-09-08): a failing reconnect loop inside the zone made Angular run change detection
    // on every attempt. Only message delivery needs the zone, and it re-enters it via ngZone.run().
    this.ngZone.runOutsideAngular(() => {
      const client = new Client({
        reconnectDelay: DashboardSocketService.BASE_RECONNECT_MS,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
      });

      if (Capacitor.isNativePlatform()) {
        // Same-origin: iOS WKWebView drops the HttpOnly cookie on a cross-site WebSocket to
        // api.lumberlinq.com, so the app connects through app.lumberlinq.com's /ws-native proxy.
        client.brokerURL = AppConstants.WEB_APP_URL.replace(/^http/, 'ws') + '/ws-native';
      } else {
        const sockJsUrl = `${AppConstants.API_URL}ws`;
        client.webSocketFactory = () =>
          new SockJS(sockJsUrl, undefined, {
            // Cross-origin (app → api) — send the session cookie on SockJS's XHR transports too.
            transportOptions: {
              'xhr-streaming': { withCredentials: true },
              'xhr-polling': { withCredentials: true },
            },
          } as any);
      }

      client.onConnect = () => {
        this.logger.debug('[LiveConnection] connected');
        // Stable again: the next drop starts the back-off from the beginning.
        this.backoffMs = DashboardSocketService.BASE_RECONNECT_MS;
        client.reconnectDelay = DashboardSocketService.BASE_RECONNECT_MS;
        if (!this.wanted || this.client !== client) { client.deactivate(); return; }
        client.subscribe('/user/queue/dashboard-updates', (message: Message) => {
          this.ngZone.run(() => this.handleMessage(message));
        });
        client.subscribe('/user/queue/notifications', (message: Message) => {
          this.ngZone.run(() => {
            this.notificationSubject.next(message.body);
            this.bridgeNotification(message.body);
          });
        });
        client.subscribe('/user/queue/progress', (message: Message) => {
          try {
            const p = JSON.parse(message.body) as LiveProgress;
            if (p?.progressId) this.ngZone.run(() => this.progressSubject.next(p));
          } catch { /* not ours / malformed — ignore */ }
        });
        // A new connection has no interest markers yet — re-announce every section the open screens need.
        this.markerSubs.clear();
        this.interest.forEach((_n, section) => this.subscribeMarker(section));
        const first = !this.everConnected;
        const wasLost = this.lostConnection;
        this.everConnected = true;
        this.lostConnection = false;
        liveTrace('live connection', 'event', first ? 'connected' : 'reconnected');
        if (!this.versionsKnown) this.fetchBaseline();
        else if (wasLost) this.catchUp('reconnected');
        this.ngZone.run(() => this.connectedSubject.next({ first: first || wasLost }));
      };
      client.onWebSocketClose = () => {
        if (!this.hasSession()) {
          // signed out meanwhile: stop retrying a connection that can only be refused
          if (this.client === client) this.client = null;
          this.ngZone.runOutsideAngular(() => { try { client.deactivate(); } catch { /* ignore */ } });
          return;
        }
        if (this.everConnected) this.lostConnection = true;
        // Back off on repeated failure, capped at 25 s. ±20 % jitter, so when the server restarts every open
        // app does not knock at the same instant (which is what keeps a recovering server unstable).
        this.backoffMs = Math.min(this.backoffMs * 2, DashboardSocketService.MAX_RECONNECT_MS);
        client.reconnectDelay = Math.round(this.backoffMs * (0.8 + Math.random() * 0.4));
        this.probeSession();
      };
      client.onStompError = (frame: IFrame) =>
        this.logger.warn('[LiveConnection] STOMP error', frame.headers?.['message']);
      client.onWebSocketError = () =>
        this.logger.debug('[LiveConnection] socket error, will retry per reconnectDelay');

      this.client = client;
      client.activate();
    });
  }

  /** Page-level listeners, attached once per page load (never removed — guarded by `wanted`). */
  private registerListeners(): void {
    if (this.listenersOn) return;
    this.listenersOn = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || !this.wanted) return;
      this.reconnectNow('tab visible');
      if (this.client?.connected) this.catchUp('tab visible');
    });
    window.addEventListener('online', () => this.reconnectNow('network back'));
    window.addEventListener('offline', () => {
      // No retries while offline (battery / pointless) — `online` above reconnects at once.
      if (!this.client) return;
      liveTrace('live connection', 'event', 'offline — retries paused');
      const c = this.client;
      this.client = null;
      if (this.everConnected) this.lostConnection = true;
      this.ngZone.runOutsideAngular(() => { try { c.deactivate(); } catch { /* ignore */ } });
    });
    if (Capacitor.isNativePlatform()) {
      App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive || !this.wanted) return;
        this.ngZone.run(() => {
          this.reconnectNow('app resumed');
          if (this.client?.connected) this.catchUp('app resumed');
        });
      });
    }
  }

  // ── Change numbers (catch-up) ─────────────────────────────────────────────

  private remember(bootId: string | undefined | null, versions: Record<string, number> | undefined | null): void {
    if (bootId) this.bootId = bootId;
    if (versions) this.versions = { ...this.versions, ...versions };
    this.versionsKnown = true;
  }

  private fetchBaseline(): void {
    this.http.get<LiveVersions>(DashboardSocketService.VERSIONS_URL).subscribe({
      next: (r) => this.remember(r?.bootId, r?.versions),
      error: () => { /* older server without the endpoint — catch-up falls back to RESYNC */ },
    });
  }

  /** Reload only what changed while we were not listening. */
  private catchUp(reason: string): void {
    if (!this.wanted || this.catchUpInFlight) return;
    this.catchUpInFlight = true;
    this.http.get<LiveVersions>(DashboardSocketService.VERSIONS_URL).subscribe({
      next: (r) => {
        this.catchUpInFlight = false;
        const next = r?.versions ?? {};
        if (!this.versionsKnown || (this.bootId && r?.bootId && r.bootId !== this.bootId)) {
          this.remember(r?.bootId, next);
          this.emitResync(reason + (this.bootId ? ' — server restarted' : ''));
          return;
        }
        const moved = Object.keys(next).filter((k) => next[k] !== (this.versions[k] ?? 0)) as DashboardEventSection[];
        this.remember(r?.bootId, next);
        if (!moved.length) { liveTrace('live connection', 'event', `catch-up: ${reason} — nothing changed`); return; }
        liveTrace('live connection', 'event', `catch-up: ${reason} — ${moved.join(', ')}`);
        this.ngZone.run(() => {
          // resync:true only so tally-id listeners reload (a catch-up carries no tally ids).
          this.changesSubject.next({ sections: moved, tallyMasterIds: [], resync: moved.includes('TALLY') });
          moved.forEach((s) => this.eventsSubject.next(s));
        });
      },
      error: () => {
        this.catchUpInFlight = false;
        this.emitResync(reason + ' (change numbers unavailable)');
      },
    });
  }

  private emitResync(reason: string): void {
    if (!this.wanted) return;
    liveTrace('live connection', 'event', 'catch-up (all): ' + reason);
    this.logger.debug('[LiveConnection] resync', reason);
    this.ngZone.run(() => {
      this.changesSubject.next({ sections: ['RESYNC'], tallyMasterIds: [], resync: true });
      this.eventsSubject.next('RESYNC');
    });
  }

  // ── Safety net: the bell's channel also triggers the screen reload ────────────────────────────
  // The bell (/user/queue/notifications) normally arrives on the SAME connection as the screen-reload message, so this only
  // helps when the connection is up but the reload message is lost — it cannot help while the connection itself is refused. The screen-reload message
  // (/user/queue/dashboard-updates) normally arrives right next to it; if it does NOT (an interest marker lost, a
  // dropped frame, a server path that publishes the bell message only) the screen used to stay stale until the user left
  // and came back. So when a notification of one of these types arrives, the sections it concerns are announced as
  // changed — but only if no real push for that section came in the next 1.5 s (no double reload in the normal case).
  private static readonly NOTIFICATION_SECTIONS: Record<string, DashboardEventSection[]> = {
    PROCESS_RUN_ASSIGNED: ['MY_TASKS', 'MILL'],
    PROCESS_RUN_ACCEPTED: ['MY_TASKS', 'MILL'],
    PROCESS_RUN_DECLINED: ['MY_TASKS', 'MILL'],
    PROCESS_RUN_STARTED: ['MY_TASKS', 'MILL'],
    PROCESS_RUN_COMPLETED: ['MY_TASKS', 'MILL', 'INVENTORY'],
    PROCESS_RUN_OUTPUT_EDITED: ['MY_TASKS', 'MILL', 'INVENTORY'],
    BP_CREATED: ['MASTER_DATA'], BP_UPDATED: ['MASTER_DATA'], BP_DELETED: ['MASTER_DATA'],
    PRODUCT_CREATED: ['MASTER_DATA'], PRODUCT_UPDATED: ['MASTER_DATA'], PRODUCT_DELETED: ['MASTER_DATA'],
    SHIPMENT_CREATED: ['CONSIGNMENT'], SHIPMENT_STATUS_CHANGED: ['CONSIGNMENT'], SHIPMENT_LOCKED: ['CONSIGNMENT'],
    SHIPMENT_TU_ADDED: ['CONSIGNMENT'], SHIPMENT_TU_REMOVED: ['CONSIGNMENT'],
    // bank transfer / cheque: the bell is the fallback if the live push is missed
    OFFLINE_PAYMENT_SUBMITTED: ['OFFLINE_PAY'], OFFLINE_PAYMENT_APPROVED: ['OFFLINE_PAY'],
    OFFLINE_PAYMENT_REJECTED: ['OFFLINE_PAY'], OFFLINE_PAYMENT_REVERSED: ['OFFLINE_PAY'],
  };
  private static readonly BRIDGE_WAIT_MS = 1500;
  /** When the last REAL push of each section arrived (ms) — the safety net only acts if it did not. */
  private readonly lastPushAt = new Map<string, number>();

  private bridgeNotification(body: string): void {
    try {
      const type = JSON.parse(body)?.type as string | undefined;
      const sections = type ? DashboardSocketService.NOTIFICATION_SECTIONS[type] : undefined;
      if (!sections) return;
      const armedAt = Date.now();
      setTimeout(() => {
        // A real push within 1.5 s BEFORE the notification (it can arrive first) or AFTER it counts as "it worked".
        const missed = sections.filter(
          (sec) => (this.lastPushAt.get(sec) ?? 0) < armedAt - DashboardSocketService.BRIDGE_WAIT_MS);
        if (!missed.length) return;
        liveTrace('live connection', 'event', 'fallback from notification ' + type + ': ' + missed.join(', '));
        this.changesSubject.next({ sections: missed, tallyMasterIds: [], resync: false });
        missed.forEach((sec) => this.eventsSubject.next(sec));
      }, DashboardSocketService.BRIDGE_WAIT_MS);
    } catch {
      /* not JSON / not ours — ignore */
    }
  }

  private handleMessage(message: Message): void {
    try {
      const parsed = JSON.parse(message.body);
      // Newer servers send `sections` (a ~1 s batch); older ones only `section`.
      const sections: DashboardEventSection[] = Array.isArray(parsed?.sections) && parsed.sections.length
        ? parsed.sections
        : (parsed?.section ? [parsed.section] : []);
      if (!sections.length) return;
      // Keep the change numbers current so a later catch-up only reports what we really missed.
      if (parsed?.versions) this.remember(parsed?.bootId, parsed.versions);
      const tallyMasterIds: number[] = Array.isArray(parsed?.tallyMasterIds)
        ? parsed.tallyMasterIds.map((id: unknown) => Number(id)).filter((id: number) => !isNaN(id))
        : [];
      liveTrace('live connection', 'event', 'push: ' + sections.join(', '));
      const arrivedAt = Date.now();
      sections.forEach((sec) => this.lastPushAt.set(sec, arrivedAt));
      this.changesSubject.next({ sections, tallyMasterIds, resync: false });
      sections.forEach((s) => this.eventsSubject.next(s));
    } catch {
      this.logger.debug('[LiveConnection] malformed message', message.body);
    }
  }
}
